import api from './api';
import { Pagination, StoreCategory, StoreCollection, StoreColor, StoreCatalogFilter, StoreCatalogItem, StoreHeroShowcase, StoreProductDetail, StoreSeller, StoreProfile, StoreOrder, UpdateProfileRequest, CreateOrderRequest, UpdateStoreOrderRequest, SiteImage, StoreBanner, PaymentSettings, ToggleFavoriteResponse, StoreFavoritesFilter, StoreReview, StoreFeaturedReview, StoreReviewableProduct, CreateReviewRequest, ProductReviewsFilter, StoreInstagramMedia, VariationOption } from '../models';

const HIDDEN_CATEGORY_IDS = [43];

export const storeService = {
  getCatalog: (filter: StoreCatalogFilter) =>
    api.get<Pagination<StoreCatalogItem>>('/store/catalog', { params: filter }).then((r) => r.data),

  getProduct: (id: number) =>
    api.get<StoreProductDetail>(`/store/products/${id}`).then((r) => r.data),

  getSellers: () =>
    api.get<StoreSeller[]>('/store/sellers').then((r) => r.data),

  getCategories: () =>
    api.get<StoreCategory[]>('/store/categories').then((r) => r.data.filter((c) => !HIDDEN_CATEGORY_IDS.includes(c.id))),

  getHomeCategories: () =>
    api.get<StoreCategory[]>('/store/home-categories').then((r) => r.data.filter((c) => !HIDDEN_CATEGORY_IDS.includes(c.id))),

  getColors: () =>
    api.get<StoreColor[]>('/store/colors').then((r) => r.data),

  getVariationOptions: () =>
    api.get<VariationOption[]>('/store/variation-options').then((r) => r.data),

  getCollections: () =>
    api.get<StoreCollection[]>('/store/collections').then((r) => r.data),

  getHeroShowcase: () =>
    api.get<StoreHeroShowcase>('/store/hero-showcase').then((r) => r.data),

  getBanners: () =>
    api.get<StoreBanner[]>('/store/banners').then((r) => r.data),

  getInstagramMedia: () =>
    api.get<StoreInstagramMedia[]>('/store/instagram-media').then((r) => r.data),

  getSiteImages: () =>
    api.get<SiteImage[]>('/store/site-images').then((r) => r.data),

  getPaymentSettings: () =>
    api.get<PaymentSettings>('/store/payment-settings').then((r) => r.data),

  getProfile: () =>
    api.get<StoreProfile>('/store/profile').then((r) => r.data),

  updateProfile: (data: UpdateProfileRequest) =>
    api.put<boolean>('/store/profile', data).then((r) => r.data),

  getOrders: () =>
    api.get<StoreOrder[]>('/store/orders').then((r) => r.data),

  createOrder: (data: CreateOrderRequest) =>
    api.post<number>('/store/orders', data).then((r) => r.data),

  updateOrder: (id: number, data: UpdateStoreOrderRequest) =>
    api.put<void>(`/store/orders/${id}`, data).then((r) => r.data),

  cancelOrder: (id: number) =>
    api.delete<void>(`/store/orders/${id}`).then((r) => r.data),

  lookupCustomer: (phone: string) =>
    api
      .get<{ maskedName: string; city: string }>('/store/customers/lookup', {
        params: { phone: phone.replace(/\D/g, '') },
      })
      .then((r) => r.data),

  lookupCep: (zipCode: string) =>
    api.get<{
      street: string;
      neighborhood: string;
      city: string;
      state: string;
      zipCode: string;
      complement?: string;
    }>(`/addresses/${zipCode.replace(/\D/g, '')}`).then((r) => r.data),

  toggleFavorite: (productId: number) =>
    api.post<ToggleFavoriteResponse>(`/store/favorites/${productId}/toggle`).then((r) => r.data),

  getFavoriteIds: () =>
    api.get<number[]>('/store/favorites/ids').then((r) => r.data),

  getFavorites: (filter: StoreFavoritesFilter) =>
    api.get<Pagination<StoreCatalogItem>>('/store/favorites', { params: filter }).then((r) => r.data),

  getProductReviews: (productId: number, filter: ProductReviewsFilter) =>
    api.get<Pagination<StoreReview>>(`/store/products/${productId}/reviews`, { params: filter }).then((r) => r.data),

  getFeaturedReviews: () =>
    api.get<StoreFeaturedReview[]>('/store/reviews/featured').then((r) => r.data),

  createReview: (data: CreateReviewRequest) =>
    api.post<number>('/store/reviews', data).then((r) => r.data),

  getReviewableProducts: (orderId: number) =>
    api.get<StoreReviewableProduct[]>(`/store/orders/${orderId}/reviewable-products`).then((r) => r.data),
};
